import { api } from './api';

const userURL = '/auth';

export const userApi = api.injectEndpoints({
  endpoints: builder => ({
    getAllUsers: builder.query({
      query: () => userURL,
      providesTags: ['User'],
    }),
    addUser: builder.mutation({
      query(body) {
        return {
          url: userURL,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['User'],
    }),
    getUsersByCamp: builder.query({
      query({ _id }) {
        return {
          url: `${userURL}/fetchJudges`,
          params: {
            _id,
          },
        };
      },
    }),
    getAllUsersWithRubrics: builder.query({
      query: ({ page = 1, limit = 10, search = '', campId = null } = {}) => {
        const params = { page, limit, search };
        if (campId) params.campId = campId;
        return {
          url: `${userURL}/userWithRubrics`,
          params,
        };
      },
      providesTags: ['User'],
    }),
    getDirectors: builder.query({
      query: ({ campId } = {}) => ({
        url: `${userURL}/directors`,
        params: campId ? { campId } : {},
      }),
      providesTags: ['Director'],
    }),
    // `useUpdateUserMutation` llevaba tiempo exportado sin endpoint detrás: el hook
    // era `undefined`. Lo consume la asignación de campamentos (`camp: [...]`).
    updateUser: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${userURL}/${id}`, method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),
    // El rol y la zona viajan juntos: el servidor exige zona si el rol es `sec` y la
    // limpia al dejar de serlo. Mandarlos por separado dejaría estados imposibles.
    updateUserRole: builder.mutation({
      query: ({ id, role, zone }) => ({
        url: `${userURL}/updaterole`,
        method: 'POST',
        body: { id, role, ...(zone ? { zone } : {}) },
      }),
      invalidatesTags: ['User'],
    }),
    getPermissionsCatalog: builder.query({
      query: () => `${userURL}/permissions/catalog`,
    }),
    getUserPermissions: builder.query({
      query: id => `${userURL}/permissions/${id}`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    updateUserPermissions: builder.mutation({
      query: ({ id, grant, deny }) => ({
        url: `${userURL}/permissions/${id}`,
        method: 'PUT',
        body: { grant, deny },
      }),
      invalidatesTags: (result, error, { id }) => ['User', { type: 'User', id }],
    }),
    updateDirector: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${userURL}/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Director'],
    }),
    adminResetPassword: builder.mutation({
      query: (id) => ({ url: `${userURL}/${id}/reset-password`, method: 'POST' }),
    }),
    importDirectors: builder.mutation({
      query: ({ directors, campId }) => ({
        url: '/director/import',
        method: 'POST',
        body: { directors, campId },
      }),
      invalidatesTags: ['Director'],
    }),
    removeCampFromUser: builder.mutation({
      query: ({ userId, campId }) => ({
        url: `${userURL}/${userId}/remove-camp`,
        method: 'DELETE',
        body: { campId },
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useGetUsersByCampQuery,
  useLazyGetUsersByCampQuery,
  useGetAllUsersWithRubricsQuery,
  useGetDirectorsQuery,
  useUpdateDirectorMutation,
  useUpdateUserRoleMutation,
  useGetPermissionsCatalogQuery,
  useGetUserPermissionsQuery,
  useUpdateUserPermissionsMutation,
  useAdminResetPasswordMutation,
  useImportDirectorsMutation,
  useRemoveCampFromUserMutation,
} = userApi;
